import React, { useMemo } from 'react';
import { View, StyleSheet, StyleProp, ViewStyle, TextStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { onColor } from '@/constants/shared/theme/onColor';

export type GlassStatusVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';

export interface GlassStatusBadgeProps {
  label: string;
  variant?: GlassStatusVariant;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  /**
   * When true, renders a filled chip with high-contrast text (matches resident
   * `FilterChip` active state). When false (default), renders a tinted glass
   * pill — chip border and label both pull from the same accent token.
   */
  filled?: boolean;
}

/**
 * Theme-aware status pill for tasks, events, transactions, and any other
 * "this thing has a state" badge in admin. Backed by `semantic.status.*`
 * tokens so dark/light toggle re-themes automatically.
 */
export const GlassStatusBadge: React.FC<GlassStatusBadgeProps> = ({
  label,
  variant = 'info',
  style,
  textStyle,
  filled = false,
}) => {
  const palette = useStatusPalette();
  const tone = palette[variant];
  const styles = useStyles();

  const containerStyle = useMemo<StyleProp<ViewStyle>>(
    () => [
      styles.container,
      filled
        ? { backgroundColor: tone.solid, borderColor: tone.solid }
        : { backgroundColor: tone.bg, borderColor: tone.border },
    ],
    [styles.container, filled, tone],
  );

  const labelStyle = useMemo<StyleProp<TextStyle>>(
    () => [styles.label, { color: filled ? tone.solidText : tone.text }],
    [styles.label, filled, tone],
  );

  return (
    <View style={[containerStyle, style]}>
      <Text style={[labelStyle, textStyle]}>{label}</Text>
    </View>
  );
};

GlassStatusBadge.displayName = 'GlassStatusBadge';

interface StatusTone {
  bg: string;
  border: string;
  text: string;
  solid: string;
  solidText: string;
}

type StatusPalette = Record<GlassStatusVariant, StatusTone>;

/**
 * Centralized resolver from a status variant to a tinted-vs-filled tone.
 * Exported via the hook below so feature code can drive any swatch (table
 * cell tint, transaction amount color) from the same source as the badge.
 */
export function useStatusPalette(): StatusPalette {
  return useThemedStyles(({ semantic }) => ({
    success: tone(semantic.status.success, semantic.status.successBg),
    warning: tone(semantic.status.warning),
    error: tone(semantic.status.error),
    info: tone(semantic.status.info),
    neutral: tone(semantic.text.muted),
  }));
}

function tone(accent: string, bgOverride?: string): StatusTone {
  return {
    solid: accent,
    // Derived, not constant: success/warning accents are LIGHTER in the dark
    // theme, so a fixed white label drops to ~2:1 on them.
    solidText: onColor(accent),
    bg: bgOverride ?? withAlpha(accent, 0.16),
    border: withAlpha(accent, 0.45),
    text: accent,
  };
}

/**
 * Crude hex/RGB → rgba shifter so we can derive translucent backgrounds from
 * solid status tokens without piping every alpha through theme files.
 */
function withAlpha(color: string, alpha: number): string {
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
  }
  return color;
}

const useStyles = () =>
  useThemedStyles(() =>
    StyleSheet.create({
      container: {
        alignSelf: 'flex-start',
        borderRadius: 999,
        borderWidth: 1,
        paddingHorizontal: 10,
        paddingVertical: 4,
      },
      label: {
        fontSize: 12,
        fontWeight: '600',
        letterSpacing: 0.2,
      },
    }),
  );
